import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import { useOrganization } from '@/hooks/useOrganization';
import { toast } from '@/hooks/use-toast';
import { formatUrl } from '@/lib/utils';

interface CreateOrganizationDialogProps {
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CreateOrganizationDialog({ 
  trigger, 
  onSuccess,
  open: externalOpen,
  onOpenChange: externalOnOpenChange
}: CreateOrganizationDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  
  // Use external control if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = externalOnOpenChange || setInternalOpen;
  const [organizationName, setOrganizationName] = useState('');
  const [companyUrl, setCompanyUrl] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { createOrganization } = useOrganization();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!organizationName.trim()) {
      toast({
        title: "Error",
        description: "Organization name is required.",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsCreating(true);
      await createOrganization(
        organizationName.trim(), 
        companyUrl.trim() ? formatUrl(companyUrl.trim()) : undefined
      );
      
      const successMessage = companyUrl.trim() 
        ? `Organization "${organizationName}" created and company context generated!`
        : `Organization "${organizationName}" created successfully!`;
      
      toast({
        title: "Success",
        description: successMessage
      });
      
      setOrganizationName('');
      setCompanyUrl('');
      setOpen(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error creating organization:', error);
      toast({
        title: "Error",
        description: "Failed to create organization. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setOrganizationName('');
      setCompanyUrl('');
    }
  };

  // If externally controlled, don't render trigger
  if (externalOpen !== undefined) {
    return (
      <Dialog open={open} onOpenChange={externalOnOpenChange || handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create Organization</DialogTitle>
          <DialogDescription>
            Create a new organization to collaborate with your team. Optionally add your company URL to generate context automatically.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Organization Name</Label>
              <Input
                id="name"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Enter organization name"
                disabled={isCreating}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="companyUrl">Company URL (Optional)</Label>
              <Input
                id="companyUrl"
                type="url"
                value={companyUrl}
                onChange={(e) => setCompanyUrl(e.target.value)}
                placeholder="https://yourcompany.com"
                disabled={isCreating}
              />
              <p className="text-xs text-muted-foreground">
                We'll automatically generate company context from your website to help personalize your agents.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating}>
              {isCreating 
                ? (companyUrl.trim() ? "Creating & Generating Context..." : "Creating...") 
                : "Create Organization"
              }
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    );
  }

  // Default behavior with trigger for internal state control
  return (
    <Dialog open={open} onOpenChange={externalOnOpenChange || handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Create Organization
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create Organization</DialogTitle>
          <DialogDescription>
            Create a new organization to collaborate with your team. Optionally add your company URL to generate context automatically.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Organization Name</Label>
              <Input
                id="name"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Enter organization name"
                disabled={isCreating}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="companyUrl">Company URL (Optional)</Label>
              <Input
                id="companyUrl"
                type="url"
                value={companyUrl}
                onChange={(e) => setCompanyUrl(e.target.value)}
                placeholder="https://yourcompany.com"
                disabled={isCreating}
              />
              <p className="text-xs text-muted-foreground">
                We'll automatically generate company context from your website to help personalize your agents.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating}>
              {isCreating 
                ? (companyUrl.trim() ? "Creating & Generating Context..." : "Creating...") 
                : "Create Organization"
              }
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}